// src/pages/DifferentialExpression/hooks/useFileSelection.js
import { useState, useCallback } from "react";

export const useFileSelection = () => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedSeqType, setSelectedSeqType] = useState("riboseq");
  const [selectedGroup, setSelectedGroup] = useState("all");
  const [lastSelectedFileIndex, setLastSelectedFileIndex] = useState(null);

  const toggleFile = useCallback((fileId) => {
    const fileIdNumber = typeof fileId === "string" ? parseInt(fileId) : fileId;

    setSelectedFiles((prev) => {
      if (prev.includes(fileIdNumber)) {
        return prev.filter((id) => id !== fileIdNumber);
      } else {
        return [...prev, fileIdNumber];
      }
    });
  }, []);

  const toggleAllFiles = useCallback((fileIds, select = true) => {
    const fileIdNumbers = fileIds.map((id) =>
      typeof id === "string" ? parseInt(id) : id
    );

    if (select) {
      setSelectedFiles((prev) => {
        const newFiles = fileIdNumbers.filter((id) => !prev.includes(id));
        return [...prev, ...newFiles];
      });
    } else {
      setSelectedFiles((prev) =>
        prev.filter((id) => !fileIdNumbers.includes(id))
      );
    }
  }, []);

  const selectMultipleFiles = useCallback((fileIds) => {
    const fileIdNumbers = fileIds.map((id) =>
      typeof id === "string" ? parseInt(id) : id
    );

    setSelectedFiles((prev) => {
      const newFiles = fileIdNumbers.filter((id) => !prev.includes(id));
      return [...prev, ...newFiles];
    });
  }, []);

  const deselectMultipleFiles = useCallback((fileIds) => {
    const fileIdNumbers = fileIds.map((id) =>
      typeof id === "string" ? parseInt(id) : id
    );

    setSelectedFiles((prev) =>
      prev.filter((id) => !fileIdNumbers.includes(id))
    );
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedFiles([]);
  }, []);

  const toggleFileWithShift = useCallback(
    (fileId, isShiftPressed, fileIndex, availableFiles) => {
      const fileIdNumber =
        typeof fileId === "string" ? parseInt(fileId) : fileId;

      if (isShiftPressed && lastSelectedFileIndex !== null && availableFiles) {
        // Shift-click: select range
        const startIndex = Math.min(lastSelectedFileIndex, fileIndex);
        const endIndex = Math.max(lastSelectedFileIndex, fileIndex);
        const rangeFileIds = availableFiles
          .slice(startIndex, endIndex + 1)
          .map((f) =>
            typeof f.file_id === "string" ? parseInt(f.file_id) : f.file_id
          );

        // Check if all files in range are selected
        const allInRangeSelected = rangeFileIds.every((id) =>
          selectedFiles.includes(id)
        );

        setSelectedFiles((prev) => {
          if (allInRangeSelected) {
            // Deselect range
            return prev.filter((id) => !rangeFileIds.includes(id));
          } else {
            // Select range
            const newFiles = rangeFileIds.filter((id) => !prev.includes(id));
            return [...prev, ...newFiles];
          }
        });
      } else {
        // Normal click: toggle single file
        setSelectedFiles((prev) => {
          if (prev.includes(fileIdNumber)) {
            return prev.filter((id) => id !== fileIdNumber);
          } else {
            return [...prev, fileIdNumber];
          }
        });
        setLastSelectedFileIndex(fileIndex);
      }
    },
    [selectedFiles, lastSelectedFileIndex]
  );

  // Enhanced bulk toggle for better UX
  const bulkToggleFiles = useCallback(
    (fileIds) => {
      const fileIdNumbers = fileIds.map((id) =>
        typeof id === "string" ? parseInt(id) : id
      );
      const allSelected = fileIdNumbers.every((id) =>
        selectedFiles.includes(id)
      );

      if (allSelected) {
        // Deselect all
        setSelectedFiles((prev) =>
          prev.filter((id) => !fileIdNumbers.includes(id))
        );
      } else {
        // Select all
        setSelectedFiles((prev) => {
          const newFiles = fileIdNumbers.filter((id) => !prev.includes(id));
          return [...prev, ...newFiles];
        });
      }
    },
    [selectedFiles]
  );

  return {
    selectedFiles,
    selectedSeqType,
    selectedGroup,
    toggleFile,
    toggleFileWithShift,
    toggleAllFiles,
    selectMultipleFiles,
    deselectMultipleFiles,
    bulkToggleFiles,
    setSelectedSeqType,
    setSelectedGroup,
    clearSelection,
    lastSelectedFileIndex,
    setLastSelectedFileIndex,
  };
};
